/// <amd-module name="typeahead/core/logging/OmnitureLogger" />

import {Element} from "typeahead/core/logging/Element";
import {Leg} from "typeahead/core/logging/Leg";
import {ProblemOrigin} from "typeahead/core/logging/ProblemOrigin";
import {Severity} from "typeahead/core/logging/Severity";
import TypeaheadErrorLogger from "typeahead/core/logging/TypeaheadErrorLogger";
import {ElementFocusedMessage} from "typeahead/core/message/ElementFocusedMessage";
import {FormSubmittedMessage} from "typeahead/core/message/FormSubmittedMessage";
import {InputValueEditedMessage} from "typeahead/core/message/InputValueEditedMessage";
import {SubmitClickedMessage} from "typeahead/core/message/SubmitClickedMessage";
import {SuggestionAutoSelectedMessage} from "typeahead/core/message/SuggestionAutoSelectedMessage";
import {SuggestionSelectedMessage} from "typeahead/core/message/SuggestionSelectedMessage";
import {SuggestionShownMessage} from "typeahead/core/message/SuggestionShownMessage";
import {TypeaheadMessage} from "typeahead/core/message/TypeaheadMessage";
import {UserExitedMessage} from "typeahead/core/message/UserExitedMessage";
import {IRegionNames, ISuggestionData, Suggestion, SuggestionCategory} from "typeahead/core/Suggestion";
import {FlightType, Lob, PackageType, RailType, TypeaheadUtils} from "typeahead/core/TypeaheadUtils";
import {Optional} from "typeahead/core/util/Optional";

export enum TrackingDataTriggers {
    blur = "blur",
    submit = "submit",
}

export interface ITrackingData {
    prop16: string;
    eVar28: string;
    eVar48: string;
    prop73: string;
    prop74: string;
    eventString: string;
}

export class OmnitureLogger {

    // This utility determines which leg of the journey  esp. in multi destination flights but applicable to all
    public static getLegIndex(domElementId): number {
        const index: string = domElementId.replace(/\D/g, ""); // Replace all non digits with empty
        return index ? parseInt(index, 10) - 1 : 0;
    }

    // Utility function to return referrer information LaunchView|SrchWzd|Tab|LOB|Locale|SiteId
    public static getBasicReport(rfrrIdPrefix: string, lob: Lob, flightType: FlightType, packageType: PackageType,
                                 railType: RailType, clientId: string, locale: string, siteid: string) {
        const parts = [
            OmnitureLogger.getLaunchView(clientId),
            OmnitureLogger.getWizardName(rfrrIdPrefix, clientId),
            OmnitureLogger.getTab(lob),
            OmnitureLogger.getShortLob(lob, flightType, packageType, railType),
            locale,
            siteid,
        ];
        return parts.filter(Boolean).join("|");
    }

    private static emptyRegionNames: IRegionNames = {
        fullName : "-",
        lastSearchName : "-",
    };

    private static readonly blurTrackingApplicableClients: {[key: string]: boolean} = {
        "AWSHomepage": true,
        "Cars.Search": true,
        "CarsLandingPage": true,
        "Flights.Search": true,
        "FlightsLandingPage": true,
        "Homepage": true,
        "Hotels.Search": true,
        "HotelsLandingPage": true,
        "LX.Search": true,
        "LXLandingPage": true,
        "OpenSearchPage": true,
        "PackageLandingPage": true,
        "RailsLandingPage": true,
    };

    // empty Element suggests that no typeahead element in the form was interacted
    private static emptyElement: Element = new Element();

    private static getWizardName(rfrrIdPrefix: string, clientId: string) {
        let wizardIdentifier: string;
        if (rfrrIdPrefix) {
            switch (rfrrIdPrefix) {
                case "FLT.SR|UpdateSearch": wizardIdentifier = "UpdateSearch"; break;
                case "HOT.SR|UpdateSearch": wizardIdentifier = "UpdateSearch"; break;
                case "Open.Search|UpdateSearch": wizardIdentifier = "UpdateSearch"; break;
                default: wizardIdentifier = "CoreWzd";
            }
        } else if (clientId) {
            switch (clientId) {
                case "Flights.Search": wizardIdentifier = "UpdateSearch"; break;
                case "Hotels.Search": wizardIdentifier = "UpdateSearch"; break;
                default: wizardIdentifier = "CoreWzd";
            }
        }
        return wizardIdentifier;
    }

    private static getShortLob(lob: Lob, flightType: FlightType, packageType: PackageType, railType: RailType): string {
        switch (lob) {
            case Lob.Flight: {
                switch (flightType) {
                    case FlightType.OneWay:
                        return "F.OW";
                    case FlightType.RoundTrip:
                        return "F.RT";
                    case FlightType.MultiDestination:
                        return "F.MD";
                    default:
                        return "-";
                }
            }
            case Lob.Hotel:
                return "H";
            case Lob.VacationRental:
                return "VR";
            case Lob.Package: {
                switch (packageType) {
                    case PackageType.FlightHotelCar:
                        return "FHC";
                    case PackageType.HotelCar:
                        return "HC";
                    case PackageType.FlightCar:
                        return "FC";
                    case PackageType.FlightHotel:
                        return "FH";
                    default:
                        return "-";
                }
            }
            case Lob.Activity:
                return "LX";
            case Lob.Rail: {
                switch (railType) {
                    case RailType.OneWay:
                        return "R.OW";
                    case RailType.RoundTrip:
                        return "R.RT";
                    default:
                        return "-";
                }
            }
            case Lob.Cruise:
                return "CR";
            case Lob.Car:
                return "C";
            case Lob.OpenSearch:
                return "OS";
            default:
                return "-";
        }
    }

    // TODO : include logic for getting launchview info for HSRP and FSRP
    private static getLaunchView(clientId: string): string {
        clientId = clientId.toLowerCase();

        if (clientId.indexOf("home") > -1) {
            return "Home";
        } else if (clientId.indexOf("flight") > -1) {
            return "FLT";
        } else if (clientId.indexOf("hotels.search") > -1) {
            return "HOT.SR";
        } else if (clientId.indexOf("hotel") > -1) {
            return "HTL";
        } else if (clientId.indexOf("vacationrental") > -1) {
            return "VR";
        } else if (clientId.indexOf("pack") > -1) {
            return "PKG";
        } else if (clientId.indexOf("car") > -1) {
            return "CAR";
        } else if (clientId.indexOf("cru") > -1) {
            return "CRU";
        } else if (clientId.indexOf("lx") > -1) {
            return "LX";
        } else if (clientId.indexOf("rail") > -1) {
            return "RAIL";
        } else if (clientId.indexOf("open") > -1) {
            return "OPEN";
        }
        return clientId;
    }

    private static getTab(lob: Lob): string {
        switch (lob) {
            case Lob.Flight: return "Flights";
            case Lob.Hotel: return "Hotels";
            case Lob.Package: return "Package";
            case Lob.Car: return "Car";
            case Lob.Cruise: return "Cruise";
            case Lob.Activity: return "Activity";
            case Lob.Rail: return "Rails";
            case Lob.VacationRental: return "VacationRental";
            case Lob.OpenSearch: return "OpenSearch";
            default: return "Unknown";
        }
    }

    private static getSuggestionInfo(s: Suggestion): string {
        const searchedRegion: string = s.getSearchedRegion();
        let selectedRegion: string = s.getSelectedRegion();
        if (searchedRegion === selectedRegion) {
            selectedRegion = "-";
        }
        return s.getSourceName().orElse("-")
            + ":"
            + searchedRegion
            + "|"
            + s.getType().orElse("-")
            + "|"
            + "R#"
            + s.getRelationInfo().orElse("-")
            + "|"
            + selectedRegion
            + "|"
            + s.getRegionNames().orElse(OmnitureLogger.emptyRegionNames).lastSearchName
            + (OmnitureLogger.isSuggestionInCategory(s, SuggestionCategory.USERHISTORY) ?
                "|" + SuggestionCategory[SuggestionCategory.USERHISTORY] :
                OmnitureLogger.isSuggestionInCategory(s, SuggestionCategory.HOMEAIRPORT) ?
                "|" + SuggestionCategory[SuggestionCategory.HOMEAIRPORT] :
                OmnitureLogger.isSuggestionInCategory(s, SuggestionCategory.NEARBY_LOCATION) ?
                "|" + SuggestionCategory[SuggestionCategory.NEARBY_LOCATION] : "");
    }

    private static isSuggestionInCategory(suggestion: Suggestion, category: SuggestionCategory) {
        return SuggestionCategory[category] === suggestion.getCategory().orElse("");
    }

    private static getMultiFlightSuggestionInfo(s: Suggestion): string {
        return s.getSourceName().orElse("-")
            + ":"
            + s.getSourceId().orElse("-")
            + "|"
            + s.getType().orElse("-");
    }

    private static isFlightInvolved(lob: Lob, packageType: PackageType): boolean {
        return (Lob.Flight === lob ||
            (Lob.Package === lob &&
                (PackageType.FlightCar === packageType ||
                    PackageType.FlightHotelCar === packageType ||
                    PackageType.FlightHotel === packageType)));
    }

    private  mostRecentElementInteracted: Optional<Element>;

    private rfrrIdPrefix: string = "";
    private isAutoSelected: boolean = false;
    private activeESSTests: string;
    private legs: Leg[];
    private lastSearchOriginData: ISuggestionData = {};
    private lastSearchDestinationData: ISuggestionData = {};

    constructor() {

        this.resetLoggerState();

        /**
         * Required params for  TypeaheadMessage subscribe utility "typeahead.element_focused" and handler
         * Required params for handler "typeahead.element_focused" and <TypeaheadMessage> ElementFocusedMessage object
         */
        TypeaheadMessage.subscribe(
            ElementFocusedMessage.getTopic(), (topic: string, message: ElementFocusedMessage) => {
                this.getElement(message.getDomElementId()).setElementFocused();
            },
        );

        /**
         * Required params for  TypeaheadMessage subscribe utility "typeahead.suggestion_shown" and handler
         * Required params for handler "typeahead.suggestion_shown" and <TypeaheadMessage> SuggestionShownMessage object
         * Handler gets element based on getLegIndex & setSuggestionShown based on SuggestionShownMessage object
         * Handler sets mostRecentElementInteracted to a value enforced by Optional (non null & undefined)
         * Handler gets activeESSTests from SuggestionShownMessage
         */
        TypeaheadMessage.subscribe(
            SuggestionShownMessage.getTopic(), (topic: string, message: SuggestionShownMessage) => {
                this.getElement(message.getDomElementId()).setSuggestionShown(message.getShownSuggestions());
                this.mostRecentElementInteracted = Optional.of(this.getElement(message.getDomElementId()));
                this.activeESSTests = message.getActiveESSTests();
            },
        );

        /**
         * Required params for  TypeaheadMessage subscribe utility "typeahead.input_value_edited" and handler
         * Required params for handler "typeahead.input_value_edited" & <TypeaheadMessage> InputValueEditedMessage
         * Handler gets element based on getLegIndex & setMostRecentInputValue based on InputValueEditedMessage object
         * If newInputValue handler sets mostRecentElementInteracted to the element
         */
        TypeaheadMessage.subscribe(
            InputValueEditedMessage.getTopic(), (topic: string, message: InputValueEditedMessage) => {
                this.getElement(message.getDomElementId())
                                            .setMostRecentInputValue(message.getNewInputValue());
                if (message.getNewInputValue()) { // if the text is deleted completely -> no interaction (car dropoff)
                    this.mostRecentElementInteracted = Optional.of(this.getElement(message.getDomElementId()));
                }
            },
        );

        /**
         * Required params for  TypeaheadMessage subscribe utility "typeahead.suggestion_auto_selected" and handler
         * Required params for handler
         * "typeahead.suggestion_auto_selected" & <TypeaheadMessage> SuggestionAutoSelectedMessage
         * Handler gets element based on getLegIndex &
         * setSuggestionSelected based on SuggestionAutoSelectedMessage object
         */
        TypeaheadMessage.subscribe(
            SuggestionAutoSelectedMessage.getTopic(),
            (topic: string, message: SuggestionAutoSelectedMessage) => {
                this.getElement(message.getDomElementId()).setSuggestionSelected(message.getSuggestion(), "");
                this.isAutoSelected = true;
            },
        );

        /**
         * Required params for  TypeaheadMessage subscribe utility "typeahead.suggestion_selected" and handler
         * Required params for handler "typeahead.suggestion_selected" & <TypeaheadMessage> SuggestionSelectedMessage
         * Handler gets element based on getLegIndex & setSuggestionSelected based on SuggestionSelectedMessage object
         * Handler sets mostRecentElementInteracted to the element
         */
        TypeaheadMessage.subscribe(
            SuggestionSelectedMessage.getTopic(),
            (topic: string, message: SuggestionSelectedMessage) => {
                this.getElement(message.getDomElementId()).setSuggestionSelected(message.getSuggestion(),
                    message.getRequestId());
                this.mostRecentElementInteracted = Optional.of(this.getElement(message.getDomElementId()));
            },
        );

        /**
         * Required params for  TypeaheadMessage subscribe utility "typeahead.submit_clicked" and handler
         * Required params for handler "typeahead.submit_clicked" & <TypeaheadMessage> SubmitClickedMessage
         * Handler sets the rfrrIdPrefix based on SubmitClickedMessage
         */
        TypeaheadMessage.subscribe(
            SubmitClickedMessage.getTopic(),
            (topic: string, message: SubmitClickedMessage) => {
                this.rfrrIdPrefix = message.getRfrrIdPrefix();
                // Storefront submits page too fast so moved the logging of SF omniture request to Submit button click
                if (TypeaheadUtils.isStorefrontClient() &&
                    this.areTypeaheadInputsNonEmpty($(message.getEvent().target).parents("form:first"))) {
                    this.submitTrackingData(message.getContext(), true);
                }
            },
        );

        /**
         * Required params for  TypeaheadMessage subscribe utility "typeahead.form_submitted" and handler
         * Required params for handler "typeahead.form_submitted" & <TypeaheadMessage> FormSubmittedMessage
         * Handler submits to Omniture via submitTrackingData utility in OmnitureLogger
         */
        TypeaheadMessage.subscribe(
            FormSubmittedMessage.getTopic(),
            (topic: string, message: FormSubmittedMessage) => {
                // Storefront submits page too fast so moved the logging of SF omniture request to Submit button click
                if (!TypeaheadUtils.isStorefrontClient()) {
                    this.submitTrackingData(message.getContext(), message.isFormValid());
                }
            },
        );

        /**
         * Required params for TypeaheadMessage subscribe utility "typeahead.user_exited" and handler
         * Required params for handler "typeahead.user_exited" & <TypeaheadMessage> UserExitedMessage
         * Handler submits to Omniture via blurTrackingData utility in OmnitureLogger
         */
        TypeaheadMessage.subscribe(
            UserExitedMessage.getTopic(),
            (topic: string, message: UserExitedMessage) => {
                if (message.getDomElementId()) {
                    this.mostRecentElementInteracted = Optional.of(this.getElement(message.getDomElementId()));
                }
                this.blurTrackingData(message);
            },
        );
    }

    // NOTE: Utility function to set LOB for testing purpose only
    // To be called from *.spec.ts ONLY
    public setMostRecentElementInteracted(elem: Optional<Element>) {
        this.mostRecentElementInteracted = elem;
    }

    public getElement(domElementId: string): Element {
        const legIndex = OmnitureLogger.getLegIndex(domElementId);
        if (!this.legs[legIndex]) {
            this.legs[legIndex] = new Leg();
        }
        return this.legs[legIndex].getElement(domElementId);
    }

    public getEvar48(lob: Lob, flightType: FlightType): string {
        if (lob === Lob.Flight && flightType === FlightType.MultiDestination) {
            return this.getMultiFlightEvar48();
        } else {
            const destination: Element = this.legs[0].getDestinationElement();
            if (destination.getMostRecentSelection().isPresent()) {
                const suggestion = destination.getMostRecentSelection().get();
                if (this.shouldGetSuggestionInfo(suggestion)) {
                    return OmnitureLogger.getSuggestionInfo(suggestion);
                }
            }
            const previousDestination: string = (destination.getVal() || this.lastSearchDestinationData.lastSearchName);
            return "-:" + (this.lastSearchDestinationData.hotelId || this.lastSearchDestinationData.regionId || "-") +
                "|" + (this.lastSearchDestinationData.regionType || "-") +
                "|R#-|-|" + (previousDestination || "TANoneSelected");
        }
    }

    public getProp73(lob: Lob, flightType: FlightType, packageType: PackageType): string {
        if (Lob.Flight === lob && flightType === FlightType.MultiDestination) {
            return this.getMultiFlightProp73();
        } else if (OmnitureLogger.isFlightInvolved(lob, packageType) || Lob.Car === lob || Lob.Rail === lob) {
            let prop73: string = "";
            const origin: Element = this.legs[0].getOriginElement();
            if (origin.getMostRecentSelection().isPresent()) {
                prop73 = OmnitureLogger.getSuggestionInfo(origin.getMostRecentSelection().get());
            } else {
                const previousOrigin: string = origin.getVal() || this.lastSearchOriginData.lastSearchName;
                prop73 = "-:" + (this.lastSearchOriginData.regionId || "-") +
                    "|" + (this.lastSearchOriginData.regionType || "-") +
                    "|R#-|-|" + (previousOrigin || "TANoneSelected");
            }
            return prop73;
        } else if ( Lob.Hotel === lob || Lob.Activity === lob ||
            (Lob.Package === lob && packageType === PackageType.HotelCar)) {
            return "-:-|-|R#-|-|OriginNotApplicable";
        }
        return "";
    }

    public getProp74(lob: Lob, flightType: FlightType, requestId?: string) {
        const element: Element = this.mostRecentElementInteracted.orElse(OmnitureLogger.emptyElement);

        let prop74: string = `"${element.getMostRecentInputValue()}"`;
        // In the following if..else block, requestId will not be undefined only from UserExitedMessage
        // in which case, it is not taken from mostRecentElementInteracted, as there is a chance that the user hasn't
        // selected any option and the requestId from mostRecentElementInteracted will be null. (typeahead-blur event).
        // In every other case (message), the requestId is populated from mostRecentElementInteracted.
        if (Optional.ofNullable(requestId).isPresent()) {
            prop74 = `${prop74}|${requestId}`;
        } else if (element.getRequestId()) {
            prop74 = `${prop74}|${element.getRequestId()}`;
        } else {
            prop74 = `${prop74}|""`;
        }

        if (lob === Lob.Flight && flightType === FlightType.MultiDestination) {
            const origins: string[] = [];
            const destinations: string[] = [];
            for (const leg of this.legs) {
                origins.push(leg.getOriginElement().getVal());
                destinations.push(leg.getDestinationElement().getVal());
            }
            prop74 += "|" + origins.join("#") + "|" + destinations.join("#");
        }
        return prop74;
    }

    // capture the Typeahead triggers
    public getTypeaheadTrigger(): string {
        let trigger: string = "";
        if (!this.mostRecentElementInteracted.isPresent()) {
            if (this.isAutoSelected) {
                trigger = "TANoShow.TAAutoSelection";
            } else {
                trigger = "TANoShow.TAPrevSearch";
            }
        } else if (!this.mostRecentElementInteracted.get().isSuggestionShown()) {
            if (this.isNearbyCurrentLocationSelection()) {
                trigger = "TANoShow.TAAutoSelection";
            } else {
                trigger = "TANoShow";
                if (!this.mostRecentElementInteracted.get().getMostRecentInputValue()) {
                    trigger += ".TAPrevSearch";
                }
            }
        } else {
            /*
            Focus Trigger Typeahead : TAShow.TAFocus ( Focus on input element  &&
                (no text or previously populated text) ) )  (No user interaction except focus on input box)
            x Character Typeahead and user selection from Typeahead : TAShow.TASelection
                ( Focus on input element + >= 0 character input and selection )
            User specific string, i.e., the user didn’t select anything from the Typeahead but Submitted with the user
                specific string : x Character Typeahead and NO user selection from Typeahead : TAShow.TANoSelection
                ( Focus on input element +>0 character input but no selection)
            User submitted autopopulated String without interacting with Typeahead (TANoShow.TAPrevSearch)
            User submitted autopopulated String after interacting with typeahead (TAShow.TAPrevSearch)
            */

            trigger = "TAShow";

            if (this.mostRecentElementInteracted.get().isSuggestionSelected()) {
                if (!this.mostRecentElementInteracted.get().getMostRecentInputValue()) {
                    trigger += ".TAFocus";
                } else {
                    trigger += ".TASelection";
                }
            } else if (this.mostRecentElementInteracted.get().getMostRecentInputValue() ||
                !this.mostRecentElementInteracted.get().getVal()) {
                /* TAShow.TANoSelection : If Typeahead is shown and user submits a manual search query not equal
                    to previously populated search text
                   For CARS lob, in dropoff input, if typeahead is shown and no selection is made
                 */
                trigger += ".TANoSelection";
            } else {
                trigger += ".TAPrevSearch";
            }
        }
        return trigger;
    }

    // Function to generate Typeahead related event string
    public getEventString(): string {
        if (this.mostRecentElementInteracted.isPresent() &&
            this.mostRecentElementInteracted.get().isSuggestionShown()) {
            const userTypedQuery: string = this.mostRecentElementInteracted.get().getMostRecentInputValue();
            let eventStr: string = "event45";

            if (userTypedQuery) {
                eventStr = "event46=" + userTypedQuery.length + "," + eventStr;
            } else {
                eventStr = "event46=0," + eventStr;
            }

            if (this.mostRecentElementInteracted.get().getMostRecentSelection().isPresent()) {
                eventStr = "event44=" +
                    this.mostRecentElementInteracted.get().getMostRecentSelection().get().getPosition()
                    + "," + eventStr;
            }
            return eventStr;
        }
        return "";
    }

    public getHierarchyCode(): string {
        return this.mostRecentElementInteracted.orElse(OmnitureLogger.emptyElement)
            .getMostRecentSelection().isPresent() ?
            this.mostRecentElementInteracted.get().getMostRecentSelection().get()
                .getHierarchyCode()
            : "DL#-";
    }

    public getSelectedSuggestionPostion(): string {
        return this.mostRecentElementInteracted.orElse(OmnitureLogger.emptyElement)
            .getMostRecentSelection().isPresent() ?
            this.mostRecentElementInteracted.get().getMostRecentSelection().get().getPosition().toString()
            : "-";
    }

    public getInstrumentationValue(lob: Lob): string {
        return [

            // Vijayalaxmi Mohapatra (vmohapatra) : Abbreviate the tag names as per below
            // L = Level of selection
            // ESS = Number of suggestions sent by ESS, always present as ESS is the primary service provider for TA
            // UH = Number of suggestions from user history

            // Hierarchy info or Level of selection from the suggestions shown for LOBs other than hotel or flight
            [Lob.Flight, Lob.Hotel].indexOf(lob) > -1 ? this.getHierarchyCode() :
                "L#" + this.getSelectedSuggestionPostion(),

            // Search Type
            // "ST#" + (model.get("TASearchType") ? model.get("TASearchType") : "-"),

            // ESS Suggestion count
            "ESS#" +
                this.mostRecentElementInteracted.orElse(OmnitureLogger.emptyElement).getEssSuggestionCount(),

        ].filter(Boolean).join("|");
    }

    public getTADetails(lob: Lob, logtrigger?: string): string {

        const trigger = this.getTypeaheadTrigger();
        const instrumentationValue = this.getInstrumentationValue(lob);

        const parts = [
            trigger,
            instrumentationValue,
        ];

        // TODO : Indicate a type of suggestions shown for blur
        if (TrackingDataTriggers.blur === logtrigger) {
            parts.push(this.mostRecentElementInteracted.get().getElemId());
        }
        return parts.filter(Boolean).join("|");
    }

    public getTrackingData(isFormValid: boolean, context: IContext, trigger?: string,
                           requestId?: string): ITrackingData {
        const lob: Lob = TypeaheadUtils.getLob();
        const flightType: FlightType = TypeaheadUtils.getFlightType(lob);
        const railType: RailType = TypeaheadUtils.getRailType(lob);
        const packageType: PackageType = TypeaheadUtils.getPackageType();
        const clientId: string = TypeaheadUtils.getClientId();
        let trackingData: ITrackingData;
        this.populateLastSearchData();
        if (isFormValid || TrackingDataTriggers.blur === trigger) {
            trackingData =
                this.getValidFormTrackingdata(context, lob, flightType, packageType,
                    railType, clientId, trigger, requestId);
        } else {
            trackingData = this.getInvalidFormTrackingdata(context, lob, flightType, packageType, railType, clientId);
        }

        return trackingData;
    }

    public resetLoggerState(): void {
        this.mostRecentElementInteracted = Optional.empty<Element>();
        this.legs = [new Leg()];
        this.activeESSTests = "";
    }

    private submitTrackingData(context: IContext, isFormValid: boolean): void {
        try {
            if (isFormValid) {
                const trackingData: ITrackingData =
                    this.getTrackingData(true, context, TrackingDataTriggers.submit);

                TypeaheadUtils.submitToOmniture("wizard-submit", trackingData, this.activeESSTests);
                this.resetLoggerState();
            } else {
                // TODO Cleanup: This scenario never occurs coz the submit message is not publsh if form is invalid
                const trackingData: ITrackingData =
                    this.getTrackingData(false, context, TrackingDataTriggers.submit);
                TypeaheadUtils.submitToOmniture("wizard-submit", trackingData, null);
                this.resetLoggerState();
            }
        } catch (e) {
            TypeaheadErrorLogger.log(new Error("Error occurred while submitting typeahead tracking data to Omniture"),
                Severity.Excusable, ProblemOrigin.UniversalTypeahead);
            console.error("Error occurred while submitting typeahead tracking data to Omniture.\nCause:\n" + e.stack);
        }
    }

    private blurTrackingData(message: UserExitedMessage): void {
        //
        const context: IContext = message.getContext();
        const currentClientId: string = TypeaheadUtils.getClientId();
        const currentInputId: string = message.getDomElementId();
        const requestId: string = message.getRequestId();

        // Send blur to omniture for given clients only and
        // ONLY if the blur has been triggered after typeahead interaction
        if (OmnitureLogger.blurTrackingApplicableClients[currentClientId] && currentInputId) {
            const blurTrackingData: ITrackingData =
                this.getTrackingData(undefined, context, TrackingDataTriggers.blur, requestId);
            TypeaheadUtils.submitToOmniture("typeahead-blur", blurTrackingData, null);

            const preSubmitRfrr: string =
                blurTrackingData.eVar28.substring(0, blurTrackingData.prop16.lastIndexOf("|"));

            blurTrackingData.eVar28 = preSubmitRfrr;
            blurTrackingData.prop16 = preSubmitRfrr;
        }
    }

    private getValidFormTrackingdata(context: IContext, lob: Lob, flightType: FlightType, packageType: PackageType,
                                     railType: RailType, clientId: string, trigger?: string,
                                     requestId?: string): ITrackingData {
        this.populateNonInteractedElements();

        let basicReport: string = "";
        // The rfrrIdPrefix is not passed by corewizard
        if (!this.rfrrIdPrefix) {
            basicReport = OmnitureLogger.getBasicReport(this.rfrrIdPrefix, lob, flightType, packageType,
                railType, clientId, context.locale, context.siteid);
        } else {  // rfrrIdPrefix is passed by HSRP and FSRP
            basicReport = this.rfrrIdPrefix + "|" +
                    OmnitureLogger.getTab(lob) + "|" +
                OmnitureLogger.getShortLob(lob, flightType, packageType, railType) + "|" + context.locale
                + "|" + context.siteid;
        }
        const detailedInfo: string = basicReport + "|" + this.getTADetails(lob, trigger);

        let trackingData: ITrackingData;
        trackingData = {
            eVar28: detailedInfo,
            eVar48: this.getEvar48(lob, flightType),
            eventString: this.getEventString(),
            prop16: detailedInfo,
            prop73: this.getProp73(lob, flightType, packageType),
            prop74: this.getProp74(lob, flightType, requestId),
        };

        return trackingData;
    }

    // TODO Cleanup: This scenario never occurs coz the submit message is not publsh if form is invalid
    private getInvalidFormTrackingdata(context: IContext, lob: Lob, flightType: FlightType, packageType: PackageType,
                                       railType: RailType, clientId: string): ITrackingData {
        let trackingData: ITrackingData;
        if (this.rfrrIdPrefix) { // For search result pages
            const formInvalidRfrrId: string = this.rfrrIdPrefix + "|" +
                OmnitureLogger.getShortLob(lob, flightType, packageType, railType)
                + "|" + context.locale + "|" +
                context.siteid + "|Form-Invalid";
            trackingData = {
                eVar28: formInvalidRfrrId,
                prop16: formInvalidRfrrId,
            } as ITrackingData;
        } else {
            const formInvalidRfrrId: string =
                OmnitureLogger.getBasicReport(
                    this.rfrrIdPrefix, lob, flightType, packageType, railType,
                    clientId, context.locale, context.siteid) + "|Form-Invalid";
            trackingData = {
                eVar28: formInvalidRfrrId,
                prop16: formInvalidRfrrId,
            } as ITrackingData;
        }
        return trackingData;
    }

    private getMultiFlightEvar48(): string {
        let evar48: string = "";
        for (const leg of this.legs) {
            evar48 += (leg.getDestinationElement().getMostRecentSelection().isPresent() ?
                    OmnitureLogger.getMultiFlightSuggestionInfo(
                        leg.getDestinationElement().getMostRecentSelection().get()) : "-:-|-") + "#";
        }
        return evar48.slice(0, -1); // remove the last "#" character
    }

    private getMultiFlightProp73(): string {
        let prop73: string = "";
        for (const leg of this.legs) {
            prop73 += (leg.getOriginElement().getMostRecentSelection().isPresent() ?
                    OmnitureLogger.getMultiFlightSuggestionInfo(
                        leg.getOriginElement().getMostRecentSelection().get()) : "-:-|-") + "#";
        }
        return prop73.slice(0, -1); // remove the last "#" character
    }

    private populateNonInteractedElements(): void {
        $("input[data-provide=utypeahead]:visible").each((index, domElement) => {
            const domElementId: string = $(domElement).attr("id");
            this.getElement(domElementId);
        });
    }

    private areTypeaheadInputsNonEmpty($form: JQuery): boolean {
        return $form.find("input.gcw-required[data-provide=utypeahead]:visible").filter((index, element) => {
            return !$(element).val();
        }).length === 0;
    }

    private isNearbyCurrentLocationSelection() {
        if (!this.isAutoSelected) {
            return false;
        }
        if (!this.mostRecentElementInteracted.isPresent()) {
            return false;
        }
        if (!this.mostRecentElementInteracted.get().getMostRecentSelection().isPresent()) {
            return false;
        }
        if (TypeaheadUtils.getLob() !== Lob.Activity) {
            return false;
        }
        return this.isNearbyLocationSuggestion(this.mostRecentElementInteracted.get().getMostRecentSelection().get());
    }

    private isNearbyLocationSuggestion(suggestion: Suggestion) {
        return suggestion
            && suggestion.getCategory().isPresent()
            && suggestion.getCategory().get() === "NEARBY_LOCATION";
    }

    private shouldGetSuggestionInfo(suggestion: Suggestion) {
        return TypeaheadUtils.getLob() === Lob.Activity || !this.isNearbyLocationSuggestion(suggestion);
    }

    private populateLastSearchData() {
        const lastSearchDestinationJson: string =
            $("form:visible").find("input[data-gcw-storeable-name=gcw-destination-ta]").val();
        const lastSearchOriginJson: string =
            $("form:visible").find("input[data-gcw-storeable-name=gcw-origin-ta]").val();
        if (lastSearchDestinationJson) {
            this.lastSearchDestinationData = JSON.parse(lastSearchDestinationJson);
        }
        if (lastSearchOriginJson) {
            this.lastSearchOriginData = JSON.parse(lastSearchOriginJson);
        }
        this.lastSearchDestinationData.lastSearchName =
            $("form:visible").find("input[data-gcw-storeable-name=gcw-destination]").val();
        this.lastSearchOriginData.lastSearchName =
            $("form:visible").find("input[data-gcw-storeable-name=gcw-origin]").val();
    }
}
